import { useEffect, useState } from "react";
import { Download, Fingerprint } from "lucide-react";
import { API, api, fmtTime, withPersona } from "@/lib/api";
import { ActivityLog } from "@/components/meridian/ActivityLog";

export function AuditPanel({ task, version }) {
  const [pkg, setPkg] = useState(null);
  useEffect(() => { if (task.facts.length) api.get(`/tasks/${task.id}/evidence`).then((r) => setPkg(r.data)); }, [task.id, task.facts.length, version]);
  const download = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(pkg, null, 2)], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${pkg.package_id}.json`; a.click(); URL.revokeObjectURL(url);
  };
  const overrides = task.override_history || [];
  return (
    <div className="p-6 space-y-5" data-testid="audit-panel">
      {pkg && (
        <div className="panel">
          <div className="px-4 py-3 border-b border-slate-200 flex justify-between items-center">
            <span className="eyebrow flex items-center gap-1.5"><Fingerprint className="w-3 h-3" />Reproducible evidence package</span>
            <div className="flex gap-2">
            <a data-testid="audit-committee-pack" href={withPersona(`${API}/tasks/${task.id}/committee-pack.pdf`)} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-sky-600 text-white hover:bg-sky-700"><Download className="w-3.5 h-3.5" />Committee pack PDF</a>
            <button data-testid="download-evidence-button" onClick={download} className="text-xs flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-sky-500/50 text-sky-700 hover:bg-sky-600/10"><Download className="w-3.5 h-3.5" />Download JSON</button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-px bg-slate-100">
            <div className="bg-white p-4 space-y-1">
              <div className="num text-sm text-slate-900" data-testid="evidence-package-id">{pkg.package_id}</div>
              <div className="num text-[10px] text-slate-500 break-all">sha256 {pkg.sha256}</div>
              <div className="pt-2 space-y-1">{Object.entries(pkg.versions).map(([k, v]) => <div key={k} className="text-[11px]"><span className="text-slate-500">{k.replace(/_/g, " ")}:</span> <span className="num text-slate-700">{v}</span></div>)}</div>
            </div>
            <div className="bg-white p-4">
              <div className="eyebrow mb-2">Data versions</div>
              {pkg.documents.map((d) => <div key={d.id} className="text-[11px] flex justify-between gap-2 py-0.5"><span className="text-slate-700 truncate">{d.name}</span><span className="num text-slate-500 shrink-0">{d.effective_date || "-"} · {d.sha256.slice(0, 10)}</span></div>)}
              <div className="eyebrow mt-3 mb-1">Contents</div>
              <div className="text-[11px] num text-slate-600">{pkg.facts.length} facts · {pkg.assumptions.length} assumptions · {Object.keys(pkg.metrics).length} calculations · {pkg.policies_applied.length} policies · {pkg.scenarios.length} scenarios · {pkg.decisions.length} decisions · {pkg.activity.length} log entries</div>
            </div>
          </div>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="panel p-4" data-testid="audit-overrides">
          <div className="eyebrow mb-2">Override history ({overrides.length})</div>
          {!overrides.length && <div className="text-xs text-slate-500">No assumption overrides.</div>}
          {overrides.map((o, i) => <div key={i} className="text-xs py-0.5" data-testid={`override-history-${i}`}><span className="text-amber-800">{o.label}: {o.event}{o.value != null ? ` ${o.original_value} → ${o.value} · ${o.by} · "${o.reason}"` : ""}</span><span className="num text-[10px] text-slate-500 ml-2">{fmtTime(o.at)}</span></div>)}
        </div>
        <div className="panel p-4" data-testid="audit-decisions">
          <div className="eyebrow mb-2">Approvals & decisions ({task.decisions.length})</div>
          {!task.decisions.length && <div className="text-xs text-slate-500">No committee decision yet.</div>}
          {task.decisions.map((d) => <div key={d.id} className="text-xs text-slate-700 py-0.5">{d.decision}{d.is_override ? " (override)" : ""} · {d.reviewer} · {fmtTime(d.at)}</div>)}
        </div>
      </div>
      <div className="panel"><ActivityLog taskId={task.id} version={version} /></div>
    </div>
  );
}
